import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Autocomplete,
  Collapse,
} from "@mui/material";
import { FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";
import dayjs, { Dayjs } from "dayjs";
import ITopupDateRangePicker from "../../Components/ITopupDateRangePicker";
import { useEffect, useState, useCallback } from "react";
import { useLazyGetCustomersQuery } from "../../Services/CustomerService";
import DateRangeFilter from "../DateRangeFilter";
import ProductDenominationFilter from "../ProductDenominationFilter";
import { useGetApiTransactionFilterOptionsQuery } from "../../Services/ReportsService";

export interface SalesFilters {
  startDate?: string;
  endDate?: string;
  productType: string;
  orderNumber: string;
  batchNumber: string;
  customerName?: string;
  /** Supplier the call went to. Matched case-insensitively on the server. */
  supplier?: string;
  subSupplier?: string;
  productName?: string;
  /** Chosen from the dropdown, not typed. */
  productId?: string;
  /** The SKU of a denomination of that product. Depends on productId. */
  productDenominationsku?: string;
  /** How the supplier call ended: success, pending or failed. */
  callResult?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: SalesFilters) => void;
  onClearFilters: () => void;
  filters: SalesFilters;
  onFilterChange: (name: keyof SalesFilters, value: any) => void;
  
}

const SalesReportsSlidingFilter: React.FC<Props> = ({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  filters,
  onFilterChange,
}) => {
  const theme = useTheme();
  // Suppliers, sub-suppliers and products are read from the data rather than
  // listed here, so the panel cannot offer a choice that returns nothing.
  const { data: filterOptions } = useGetApiTransactionFilterOptionsQuery();
  const options = filterOptions?.data || {};

  /*
   * The sub-supplier accounts belonging to one supplier.
   *
   * Matched upper-case: purchase orders store "flixbus" where the call log
   * stores "FLIXBUS", and the supplier dropdown shows whichever spelling
   * the server folded to. With no supplier chosen, every account is
   * offered, because "All suppliers" has to mean all of their accounts too.
   */
  const hasSupplier = Boolean(filters.supplier && filters.supplier !== "All");

  const subSuppliersFor = (supplier?: string): string[] => {
    const byParent: Record<string, string[]> =
      (options as any).subSuppliersBySupplier || {};
    if (!supplier || supplier === "All") return options.subSuppliers || [];
    return byParent[String(supplier).trim().toUpperCase()] || [];
  };


  /**
   * The refinements, and whether any is already in use.
   *
   * A filter that is set but folded out of sight is worse than no folding at
   * all -- results would be narrowed with nothing on screen saying why. So the
   * section opens itself whenever one of them carries a value, and the toggle
   * counts them when it is closed.
   */
  const isSet = (value?: string) => !!value && value !== "All";
  const extraFilterCount = [
    filters.customerName,
    filters.productName,
    filters.supplier,
    filters.subSupplier,
    filters.batchNumber,
  ].filter(isSet).length;

  const [showMore, setShowMore] = useState(extraFilterCount > 0);
  useEffect(() => {
    if (extraFilterCount > 0) setShowMore(true);
  }, [extraFilterCount]);

  const [
    getCustomersTrigger,
    { data: customerData, isLoading: customerLoading }
  ] = useLazyGetCustomersQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);

  useEffect(() => {
    setCurrentPage(1);
    setAllCustomers([]);
    setHasMoreData(true);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim()) {
        setCurrentPage(1);
        setAllCustomers([]);
        setHasMoreData(true);

        getCustomersTrigger({
          filters: [],
          page: 1,
          pageSize: 50,
          sortBy: "createdAt",
          sortOrder: "desc",
          search: searchTerm.trim(),
        });
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm, getCustomersTrigger]);

  useEffect(() => {
    if (customerData?.data) {
      if (currentPage === 1) {
        setAllCustomers(customerData.data);
      } else {
        setAllCustomers((prev) => [...prev, ...customerData.data]);
      }
      const totalPages = Math.ceil(customerData.totalRecords / 20);
      setHasMoreData(currentPage < totalPages);
      setIsLoadingMore(false);
    }
  }, [customerData, currentPage]);

  const loadMoreData = useCallback(() => {
    if (!isLoadingMore && hasMoreData && !customerLoading) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      getCustomersTrigger({
        filters: [],
        page: nextPage,
        pageSize: 50,
        sortBy: "createdAt",
        sortOrder: "desc",
        search: "",
      });
    }
  }, [currentPage, hasMoreData, isLoadingMore, customerLoading, getCustomersTrigger]);

  const loadAllRecords = useCallback(() => {
    if (!customerLoading && !isLoadingMore) {
      setCurrentPage(1);
      setAllCustomers([]);
      setHasMoreData(true);

      getCustomersTrigger({
        page: 1,
        pageSize: 50,
        filters: [],
        search: "",
        sortBy: "createdAt",
        sortOrder: "desc",
      });
    }
  }, [customerLoading, isLoadingMore, getCustomersTrigger]);

  useEffect(() => {
    setSearchTerm(filters.customerName || "");
  }, [filters.customerName]);

  const searchOptions = React.useMemo(() => {
    if (!allCustomers.length) return [] as string[];
    const options: string[] = [];
    allCustomers.forEach((customer: any) => {
      if (customer?.firstName && customer?.lastName) {
        const fullName = `${customer.firstName} ${customer?.lastName} - ${customer?.email}`;
        if (!options.includes(fullName)) options.push(fullName);
      }
    });
    return options.sort();
  }, [allCustomers]);

  const getSearchValue = (term: string) => {
    if (!term) return "";
    if (term.includes(" - ")) {
      const parts = term.split(" - ");
      if (parts.length === 2) {
        const email = parts[1];
        return email;
      }
    }
    for (const customer of allCustomers) {
      const nameAndEmail = `${customer?.firstName || ""} ${customer?.lastName || ""} - ${customer?.email || ""}`.trim();
      if (nameAndEmail === term && customer?.email) {
        return customer?.email;
      }
      if (customer?.email === term) return term;
      if (customer?.phoneNumber === term) return term;
    }
    return term;
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          padding: 0,
        },
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        "& .MuiDrawer-paper": {
          overflow: "visible",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "12px 16px",
            backgroundColor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography variant="h6" component="h2">Apply Filters</Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }} size="small">
            <FaTimes />
          </IconButton>
        </Box>

        {/* Content */}
        {/* Scrolls. With every refinement open the panel is taller than the
            drawer, and without this the last controls sat under the footer
            with no way to reach them. */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          {/*
            Four filters people reach for, and the rest folded away.

            All nine sat in one flat column of identical dropdowns, so nothing
            looked more important than anything else and the panel ran past the
            bottom of the drawer. These four answer most questions; the others
            are refinements and open on request -- and open by themselves when
            one of them is already set, so a filter can never be active and
            out of sight.
          */}
          <TextField
            label="Order Number"
            placeholder="Search by Order number"
            variant="outlined"
            size="small"
            value={filters.orderNumber}
            onChange={(e) => onFilterChange("orderNumber", e.target.value)}
            type="search"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Product Type</InputLabel>
            <Select
              value={filters.productType}
              label="Product Type"
              onChange={(e) => onFilterChange("productType", e.target.value)}
              MenuProps={{ PaperProps: { sx: { zIndex: 9999 } }, disablePortal: true }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="phone-refil">Phone-refil</MenuItem>
              <MenuItem value="cards">Cards</MenuItem>
              <MenuItem value="travel">Travel</MenuItem>
            </Select>
          </FormControl>

          {/* The question this screen exists to answer. */}
          <FormControl fullWidth size="small">
            <InputLabel>Call Result</InputLabel>
            <Select
              value={filters.callResult || "All"}
              label="Call Result"
              onChange={(e) => onFilterChange("callResult", e.target.value)}
              MenuProps={{ PaperProps: { sx: { zIndex: 9999 } }, disablePortal: true }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>

          <DateRangeFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(key, value) => onFilterChange(key as keyof SalesFilters, value)}
            label="Date Range"
            onValidityChange={setIsDateRangeValid}
          />

          <Divider textAlign="left" sx={{ my: 0.5 }}>
            <Button
              size="small"
              onClick={() => setShowMore((v) => !v)}
              endIcon={showMore ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              {showMore ? "Fewer filters" : "More filters"}
              {!showMore && extraFilterCount > 0 ? ` (${extraFilterCount})` : ""}
            </Button>
          </Divider>

          <Collapse in={showMore} unmountOnExit>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Customer */}
              <Box>
                <Autocomplete
                  freeSolo
                  options={searchOptions}
                  value={filters.customerName || ''}
                  onInputChange={(event, newInputValue) => {
                    const searchValue = getSearchValue(newInputValue || '');
                    setSearchTerm(searchValue);
                    onFilterChange('customerName', searchValue);
                  }}
                  onChange={(event, newValue) => {
                    const searchValue = getSearchValue(newValue || '');
                    setSearchTerm(searchValue);
                    onFilterChange('customerName', searchValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Search Customer"
                      placeholder="Search by customer name"
                      onFocus={loadAllRecords}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'background.paper',
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    onScroll: (event: React.SyntheticEvent) => {
                      const listboxNode = event.currentTarget;
                      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                        loadMoreData();
                      }
                    },
                    style: { maxHeight: '200px' },
                  }}
                  sx={{
                    '& .MuiAutocomplete-popper': { zIndex: 9999 },
                  }}
                  loading={customerLoading || isLoadingMore}
                  loadingText={isLoadingMore ? 'Loading more...' : 'Loading suggestions...'}
                  noOptionsText={hasMoreData ? 'Keep typing to search...' : 'No matching results'}
                />
              </Box>


              {/*
                Product and Denomination.

                This was a single Product box listing the names seen in the
                report data, with no way to narrow to one denomination of a
                product. The pair below comes from the product catalogue
                itself, and the denomination list follows whichever product
                is chosen.
              */}
              <ProductDenominationFilter
                productId={filters.productId}
                denominationSku={filters.productDenominationsku}
                onChange={(field, value) => onFilterChange(field as any, value)}
              />

              {/*
                Sub Supplier follows Supplier, the same way Denomination
                follows Product.
                
                These two were independent lists, so Supplier=DTONE could
                sit beside Sub Supplier=SS-DING-020 -- an account that
                belongs to Ding. The pair looked related, was not, and
                returned nothing, which reads as the report being empty
                rather than as an impossible combination.
                
                Changing the supplier clears a sub-supplier that does not
                belong to it, rather than leaving a filter applied that the
                list below no longer even offers.
              */}
              <Autocomplete
                size="small"
                options={["All", ...(options.suppliers || [])]}
                value={filters.supplier || "All"}
                onChange={(e, v) => {
                  const chosen = v || "All";
                  onFilterChange("supplier", chosen);
                  const stillValid =
                    !filters.subSupplier ||
                    filters.subSupplier === "All" ||
                    subSuppliersFor(chosen).includes(filters.subSupplier);
                  if (!stillValid) onFilterChange("subSupplier", "All");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Supplier" placeholder="Type to search" />
                )}
                sx={{ "& .MuiAutocomplete-popper": { zIndex: 9999 } }}
              />

              <Autocomplete
                size="small"
                options={["All", ...subSuppliersFor(filters.supplier)]}
                value={filters.subSupplier || "All"}
                onChange={(e, v) => onFilterChange("subSupplier", v || "All")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sub Supplier"
                    placeholder={
                      hasSupplier
                        ? "Type to search"
                        : "All, or pick a supplier first"
                    }
                  />
                )}
                sx={{ "& .MuiAutocomplete-popper": { zIndex: 9999 } }}
              />

              <TextField
                label="Batch Number"
                placeholder="Search by Batch number"
                variant="outlined"
                size="small"
                value={filters.batchNumber}
                onChange={(e) => onFilterChange("batchNumber", e.target.value)}
                type="search"
              />

              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Batch Number and Sub Supplier apply to completed purchases only.
              </Typography>
            </Box>
          </Collapse>

        </Box>

        <Divider />

        {/* Footer */}
        <Box sx={{ p: 2, display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={handleClear} sx={{ flex: 1 }}>
            Clear All
          </Button>
          <Button variant="contained" onClick={handleApply} sx={{ flex: 1 }} disabled={!isDateRangeValid}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SalesReportsSlidingFilter;
